import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    // No longer locking scroll since we are using a clean dropdown instead of a full sidebar
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fallback menu items in case CMS fetch is loading or fails
  const fallbackMenu = [
    { id: '1', label: { da: 'Forside' }, url: '/', order: 1 },
    { id: '2', label: { da: 'Om Solveig' }, url: '/om-solveig', order: 2 },
    { id: '3', label: { da: 'Terapi' }, url: '/terapi', order: 3 },
    { id: '4', label: { da: 'Familiebehandling' }, url: '/terapi/familiebehandling', order: 4, parent_slug: 'terapi' },
    { id: '5', label: { da: 'Parterapi' }, url: '/terapi/parterapi', order: 5, parent_slug: 'terapi' },
    { id: '6', label: { da: 'Traumer og seksuelle overgreb' }, url: '/terapi/traumer-og-seksuelle-overgreb-i-barndommen', order: 6, parent_slug: 'terapi' },
    { id: '7', label: { da: 'Akut terapi' }, url: '/terapi/akut-terapi', order: 7, parent_slug: 'terapi' },
    { id: '8', label: { da: 'Kontakt' }, url: '/kontakt', order: 8 }
  ];

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch menu');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setMenuItems(data);
        } else {
          setMenuItems(fallbackMenu);
        }
      })
      .catch(() => {
        setMenuItems(fallbackMenu);
      });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location]);

  // Process menu items
  const sortedItems = [...(menuItems.length > 0 ? menuItems : fallbackMenu)].sort((a, b) => a.order - b.order);
  const mainItems = sortedItems.filter(item => !item.parent_slug);
  const subItems = sortedItems.filter(item => item.parent_slug === 'terapi');

  // Check if a link is active
  const isLinkActive = (url) => {
    if (url === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(url);
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white border-b border-warm-sand/30 shadow-sm' 
        : 'bg-warm-light/95 backdrop-blur-md border-b border-warm-sand/40'
    }`}>
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo / Brand Name */}
        <Link to="/" className="flex flex-col group">
          <span className="font-display text-xl md:text-2xl tracking-wide text-warm-dark group-hover:text-warm-gray transition-colors duration-300">
            Solveig Juliane Tousgaard
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-warm-gray font-medium">
            Familie- & Psykoterapeut MPF
          </span>
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {mainItems.map((item) => {
            const hasChildren = item.url === '/terapi';
            
            if (hasChildren) {
              return (
                <div key={item.id} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    className={`flex items-center gap-1.5 py-2 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-warm-dark ${
                      isLinkActive(item.url) ? 'text-warm-dark font-semibold' : 'text-warm-gray'
                    }`}
                  >
                    {item.label.da}
                    <i className={`fa-light fa-chevron-down text-xs transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div 
                      onMouseLeave={() => setIsDropdownOpen(false)}
                      className="absolute left-0 mt-2 w-72 bg-warm-light border border-warm-sand/60 rounded-lg shadow-lg py-2 animate-in fade-in slide-in-from-top-2 duration-200"
                    >
                      {subItems.map((sub) => (
                        <Link
                          key={sub.id}
                          to={sub.url}
                          className={`block px-5 py-3 text-sm hover:bg-warm-sand/30 transition-colors duration-200 ${
                            location.pathname === sub.url ? 'text-warm-dark font-semibold bg-warm-sand/20' : 'text-warm-gray'
                          }`}
                        >
                          {sub.label.da}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.id}
                to={item.url}
                className={({ isActive }) =>
                  `relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-warm-dark ${
                    isActive ? 'text-warm-dark font-semibold' : 'text-warm-gray'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label.da}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-warm-dark rounded-full animate-in fade-in zoom-in duration-300" />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}

          {/* Call to Action Button */}
          <Link
            to="/kontakt"
            className="ml-4 px-6 py-2.5 bg-warm-dark text-warm-light text-sm font-medium tracking-wide rounded-full hover:bg-warm-gray transition-all-custom shadow-sm hover:shadow-md"
          >
            Book en samtale
          </Link>
        </nav>

        {/* Mobile Menu Button (< 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="min-[1200px]:hidden p-2 text-warm-dark hover:text-warm-gray transition-colors focus:outline-none"
          aria-label={isOpen ? "Luk menu" : "Åben menu"}
        >
          <i className={`fa-light ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl transition-transform duration-200`} />
        </button>
      </div>

      {/* Mobile Navigation Dropdown (< 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden absolute top-full left-0 right-0 bg-white border-b border-warm-sand/30 shadow-lg py-6 px-6 flex flex-col gap-4 z-40 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex flex-col gap-4">
            {mainItems.map((item) => {
              const hasChildren = item.url === '/terapi';

              if (hasChildren) {
                return (
                  <div key={item.id} className="flex flex-col">
                    <button
                      onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                      className={`flex items-center justify-between w-full text-left text-base font-medium py-2 transition-colors ${
                        isLinkActive(item.url) ? 'text-warm-dark font-semibold' : 'text-warm-gray'
                      }`}
                    >
                      <span>{item.label.da}</span>
                      <i className={`fa-light fa-chevron-down text-xs transition-transform duration-300 ${
                        isMobileDropdownOpen ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    {/* Collapsible Submenu */}
                    <div className={`grid transition-all duration-300 ease-in-out ${
                      isMobileDropdownOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'
                    }`}>
                      <div className="overflow-hidden pl-4 flex flex-col gap-2.5 border-l border-warm-sand/50">
                        {subItems.map((sub) => (
                          <Link
                            key={sub.id}
                            to={sub.url}
                            onClick={() => setIsOpen(false)}
                            className={`text-sm py-1 transition-colors hover:text-warm-dark ${
                              location.pathname === sub.url ? 'text-warm-dark font-semibold' : 'text-warm-gray'
                            }`}
                          >
                            {sub.label.da}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.url}
                  onClick={() => setIsOpen(false)}
                  className={`text-base font-medium py-2 transition-colors hover:text-warm-dark ${
                    isLinkActive(item.url) ? 'text-warm-dark font-semibold' : 'text-warm-gray'
                  }`}
                >
                  {item.label.da}
                </Link>
              );
            })}

            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="mt-2 w-full py-3 bg-warm-dark text-warm-light text-center text-sm font-medium tracking-wide rounded-full hover:bg-warm-gray transition-all-custom shadow-sm hover:shadow-md"
            >
              Book en samtale
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
